import { useEffect, useMemo, useState } from 'react';
import { api, money } from '../api.js';

/**
 * Заплати — виден е само за Калин (сървърът го налага по user id, не по
 * роля; екранът тук просто не се показва в менюто за никой друг, виж
 * App.jsx). Смята се на момента при всяко отваряне — няма „спестена"
 * версия на месеца, докато не се прати фишът (тогава се пази само кога и
 * до кой имейл, в `payroll_sent_log`).
 */
export default function Payroll() {
  const [period, setPeriod] = useState(tekushtPeriod());
  const [employees, setEmployees] = useState([]);
  const [calc, setCalc] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [deliveryUsers, setDeliveryUsers] = useState([]);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.get('/api/users').then((res) => setUsers(res.items ?? res)).catch(() => setUsers([]));
    api.get('/api/payroll/delivery-users')
      .then((res) => setDeliveryUsers(res.items ?? []))
      .catch(() => setDeliveryUsers([]));
    loadEmployees();
  }, []);

  useEffect(() => { loadCalc(); loadSaved(); }, [period]);

  async function loadSaved() {
    const res = await api.get(`/api/payroll/payslips?period=${period}`);
    setSaved(res.items);
  }

  async function loadEmployees() {
    const res = await api.get('/api/payroll/employees');
    setEmployees(res.items);
  }

  async function loadCalc() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/payroll/calc?period=${period}`);
      setCalc(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    await loadEmployees();
    await loadCalc();
  }

  async function saveEmployee(id, patch) {
    await api.patch(`/api/payroll/employees/${id}`, patch);
    await refresh();
  }

  async function removeEmployee(id) {
    if (!window.confirm('Да изтрия ли този служител от заплатите? Бонусите и удръжките му по месеци също ще изчезнат.')) return;
    await api.del(`/api/payroll/employees/${id}`);
    await refresh();
  }

  async function addBonus(employeeId, label, amount) {
    await api.post('/api/payroll/bonus', { employeeId, period, label, amount: Number(amount) });
    await loadCalc();
  }
  async function removeBonus(id) {
    await api.del(`/api/payroll/bonus/${id}`);
    await loadCalc();
  }
  async function addDeduction(employeeId, label, amount) {
    await api.post('/api/payroll/deduction', { employeeId, period, label, amount: Number(amount) });
    await loadCalc();
  }
  async function removeDeduction(id) {
    await api.del(`/api/payroll/deduction/${id}`);
    await loadCalc();
  }

  async function addVacation(employeeId, from, to) {
    try {
      await api.post('/api/payroll/vacation', { employeeId, from, to });
      await loadCalc();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    }
  }
  async function removeVacation(employeeId, from, to) {
    await api.del(`/api/payroll/vacation?employeeId=${employeeId}&from=${from}&to=${to}`);
    await loadCalc();
  }

  async function savePayslip(employeeId) {
    setSavingId(employeeId);
    setMessage(null);
    try {
      const res = await api.post('/api/payroll/payslips', { employeeId, period });
      setMessage({ ok: true, text: `Фишът е записан (${money(res.total)}) — остава такъв, какъвто е сега.` });
      await loadCalc();
      await loadSaved();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setSavingId(null);
    }
  }

  async function sendPayslip(employeeId) {
    setSendingId(employeeId);
    setMessage(null);
    try {
      const res = await api.post(`/api/payroll/send/${employeeId}?period=${period}`);
      setMessage({ ok: true, text: `Записаният фиш е изпратен на ${res.to}.` });
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setSendingId(null);
    }
  }

  const [godina, mesec] = period.split('-');

  return (
    <div>
      <div className="page-head">
        <h1>Заплати</h1>
        <label className="field" style={{ margin: 0 }}>
          Период
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </label>
      </div>

      <datalist id="delivery-users">
        {deliveryUsers.map((u) => <option key={u.id} value={u.displayName} />)}
      </datalist>

      {calc && (
        <div className="note">
          Общ оборот без ДДС за {mesec}.{godina}: Стоки {money(calc.revenueGoods)} ·
          {' '}Услуги {money(calc.revenueServices)}
        </div>
      )}
      {message && <div className={message.ok ? 'success' : 'error'}>{message.text}</div>}
      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-head">
          Служители
          <button onClick={() => setAdding(true)}>+ Служител</button>
        </div>
        {adding && (
          <NewEmployeeRow
            users={users}
            onCancel={() => setAdding(false)}
            onSave={async (data) => {
              await api.post('/api/payroll/employees', data);
              setAdding(false);
              await refresh();
            }}
          />
        )}
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Име</th>
                <th>Тефтер юзър</th>
                <th>Деливери юзър</th>
                <th>Имейл</th>
                <th className="num">Основна</th>
                <th className="num">% Стоки</th>
                <th className="num">% Услуги</th>
                <th className="num">На тонер касета</th>
                <th className="num">Ваучери</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <EmployeeRow
                  key={e.id}
                  employee={e}
                  users={users}
                  editing={editingId === e.id}
                  onEdit={() => setEditingId(e.id)}
                  onCancel={() => setEditingId(null)}
                  onSave={async (patch) => { await saveEmployee(e.id, patch); setEditingId(null); }}
                  onRemove={() => removeEmployee(e.id)}
                />
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={10} className="empty">Няма добавени служители.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-head">Фишове за {mesec}.{godina}</div>
        {loading && <div className="muted" style={{ padding: 8 }}>Смятане…</div>}
        {!loading && calc?.rows.map((r) => (
          <PayslipCard
            key={r.employeeId}
            row={r}
            sending={sendingId === r.employeeId}
            saving={savingId === r.employeeId}
            onSave={() => savePayslip(r.employeeId)}
            onAddVacation={(from, to) => addVacation(r.employeeId, from, to)}
            onRemoveVacation={(from, to) => removeVacation(r.employeeId, from, to)}
            onAddBonus={(label, amount) => addBonus(r.employeeId, label, amount)}
            onRemoveBonus={removeBonus}
            onAddDeduction={(label, amount) => addDeduction(r.employeeId, label, amount)}
            onRemoveDeduction={removeDeduction}
            onSend={() => sendPayslip(r.employeeId)}
          />
        ))}
        {!loading && calc && calc.rows.length === 0 && (
          <div className="empty" style={{ padding: 12 }}>Няма активни служители.</div>
        )}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-head">Записани фишове за {mesec}.{godina}</div>
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr><th>Служител</th><th>Записан на</th><th className="num">Сума</th><th /></tr>
            </thead>
            <tbody>
              {saved.map((f) => (
                <tr key={f.id}>
                  <td>{f.employee_name}</td>
                  <td className="muted">{new Date(f.created_at).toLocaleString('bg-BG')}</td>
                  <td className="num">{money(f.total)}</td>
                  <td><a href={`/print/payslip/${f.id}`} target="_blank" rel="noreferrer">отвори</a></td>
                </tr>
              ))}
              {saved.length === 0 && (
                <tr><td colSpan={4} className="empty">Още няма записани фишове за този месец.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function tekushtPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function NewEmployeeRow({ users, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [tefterUserId, setTefterUserId] = useState('');
  const [deliveryTechnicianName, setDeliveryTechnicianName] = useState('');
  const [email, setEmail] = useState('');
  const [baseSalary, setBaseSalary] = useState('0');
  const [percentGoods, setPercentGoods] = useState('0');
  const [percentServices, setPercentServices] = useState('0');
  const [tonerRate, setTonerRate] = useState('0');
  const [foodVoucherAmount, setFoodVoucherAmount] = useState('0');

  return (
    <div className="toolbar" style={{ flexWrap: 'wrap' }}>
      <input placeholder="Име" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      <select value={tefterUserId} onChange={(e) => setTefterUserId(e.target.value)}>
        <option value="">(без Тефтер юзър)</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
      </select>
      <input
        list="delivery-users"
        placeholder="Потребител в Деливери"
        value={deliveryTechnicianName}
        onChange={(e) => setDeliveryTechnicianName(e.target.value)}
        autoComplete="off"
      />
      <input placeholder="Имейл" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
      <label className="field" style={{ margin: 0 }}>
        Основна
        <input type="number" step="0.01" style={{ width: 80 }} value={baseSalary} onChange={(e) => setBaseSalary(e.target.value)} />
      </label>
      <label className="field" style={{ margin: 0 }}>
        % Стоки
        <input type="number" step="0.001" style={{ width: 60 }} value={percentGoods} onChange={(e) => setPercentGoods(e.target.value)} />
      </label>
      <label className="field" style={{ margin: 0 }}>
        % Услуги
        <input type="number" step="0.001" style={{ width: 60 }} value={percentServices} onChange={(e) => setPercentServices(e.target.value)} />
      </label>
      <label className="field" style={{ margin: 0 }}>
        На тонер касета
        <input type="number" step="0.01" style={{ width: 70 }} value={tonerRate} onChange={(e) => setTonerRate(e.target.value)} />
      </label>
      <label className="field" style={{ margin: 0 }}>
        Ваучери
        <input type="number" step="0.01" style={{ width: 70 }} value={foodVoucherAmount} onChange={(e) => setFoodVoucherAmount(e.target.value)} />
      </label>
      <button
        disabled={!name.trim()}
        onClick={() => onSave({
          name: name.trim(),
          tefterUserId: tefterUserId ? Number(tefterUserId) : null,
          deliveryTechnicianName: deliveryTechnicianName.trim() || null,
          email: email.trim() || null,
          baseSalary, percentGoods, percentServices, tonerRate, foodVoucherAmount,
        })}
      >
        Запис
      </button>
      <button className="link" onClick={onCancel}>Отказ</button>
    </div>
  );
}

function EmployeeRow({ employee, users, editing, onEdit, onCancel, onSave, onRemove }) {
  const [form, setForm] = useState(employee);
  useEffect(() => { setForm(employee); }, [employee, editing]);

  if (!editing) {
    return (
      <tr className={employee.is_active ? '' : 'muted'}>
        <td>
          <strong>{employee.name}</strong>
          {!employee.is_active && ' (спрян)'}
        </td>
        <td className="muted">{employee.tefter_user_name ?? '—'}</td>
        <td className="muted">{employee.delivery_technician_name ?? '—'}</td>
        <td className="muted">{employee.email ?? '—'}</td>
        <td className="num">{money(employee.base_salary)}</td>
        <td className="num">{employee.percent_goods}%</td>
        <td className="num">{employee.percent_services}%</td>
        <td className="num">{money(employee.toner_rate)}</td>
        <td className="num">{money(employee.food_voucher_amount)}</td>
        <td>
          <button className="link" onClick={onEdit}>редакция</button>
          {' · '}
          <button className="link" onClick={onRemove}>изтрий</button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></td>
      <td>
        <select
          value={form.tefter_user_id ?? ''}
          onChange={(e) => setForm({ ...form, tefter_user_id: e.target.value || null })}
        >
          <option value="">(без)</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
      </td>
      <td>
        <input
          list="delivery-users"
          value={form.delivery_technician_name ?? ''}
          onChange={(e) => setForm({ ...form, delivery_technician_name: e.target.value })}
        />
      </td>
      <td><input value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></td>
      <td className="num">
        <input type="number" step="0.01" style={{ width: 80 }}
          value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} />
      </td>
      <td className="num">
        <input type="number" step="0.001" style={{ width: 60 }}
          value={form.percent_goods} onChange={(e) => setForm({ ...form, percent_goods: e.target.value })} />
      </td>
      <td className="num">
        <input type="number" step="0.001" style={{ width: 60 }}
          value={form.percent_services} onChange={(e) => setForm({ ...form, percent_services: e.target.value })} />
      </td>
      <td className="num">
        <input type="number" step="0.01" style={{ width: 70 }}
          value={form.toner_rate} onChange={(e) => setForm({ ...form, toner_rate: e.target.value })} />
      </td>
      <td className="num">
        <input type="number" step="0.01" style={{ width: 70 }}
          value={form.food_voucher_amount} onChange={(e) => setForm({ ...form, food_voucher_amount: e.target.value })} />
      </td>
      <td>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={!!form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          активен
        </label>
        <button onClick={() => onSave({
          name: form.name, tefterUserId: form.tefter_user_id ? Number(form.tefter_user_id) : null,
          deliveryTechnicianName: form.delivery_technician_name || null, email: form.email || null,
          baseSalary: form.base_salary, percentGoods: form.percent_goods, percentServices: form.percent_services,
          tonerRate: form.toner_rate, foodVoucherAmount: form.food_voucher_amount, isActive: form.is_active,
        })}>
          Запис
        </button>
        <button className="link" onClick={onCancel}>Отказ</button>
      </td>
    </tr>
  );
}

function PayslipCard({
  row, sending, saving, onSave, onAddVacation, onRemoveVacation,
  onAddBonus, onRemoveBonus, onAddDeduction, onRemoveDeduction, onSend,
}) {
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="card-head">
        {row.name}
        <div className="spacer" />
        <strong>{money(row.total)}</strong>
      </div>
      <div className="table-wrap">
        <table className="compact">
          <tbody>
            <tr><td>Основна заплата</td><td className="num">{money(row.baseSalary)}</td></tr>
            <tr>
              <td>% от Стоки ({row.percentGoods}% от {money(row.baseGoods)})</td>
              <td className="num">{money(row.fromGoods)}</td>
            </tr>
            <tr>
              <td>% от Услуги ({row.percentServices}% от {money(row.baseServices)})</td>
              <td className="num">{money(row.fromServices)}</td>
            </tr>
            <tr>
              <td>Заредени тонер касети ({row.refillCount} бр. × {money(row.tonerRate)})</td>
              <td className="num">{money(row.fromRefills)}</td>
            </tr>
            <tr>
              <td><strong>Общо бонуси</strong> <span className="muted">(% и зареждания, без основната и ваучерите)</span></td>
              <td className="num"><strong>{money(row.fromGoods + row.fromServices + row.fromRefills)}</strong></td>
            </tr>
            <tr>
              <td>
                Ваучери за храна
                {row.foodVoucherAmount !== row.foodVoucherFull && (
                  <span className="muted"> ({row.workedDays} от {row.workingDays} работни дни от {money(row.foodVoucherFull)})</span>
                )}
              </td>
              <td className="num">{money(row.foodVoucherAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <VacationEditor
        calendarDays={row.vacationDaysCalendar}
        manualDays={row.vacationDaysManual}
        total={row.vacationDays.length}
        hasDeliveryUser={!!row.deliveryTechnicianName}
        onAdd={onAddVacation}
        onRemove={onRemoveVacation}
      />
      <LineEditor title="Бонуси" lines={row.bonuses} onAdd={onAddBonus} onRemove={onRemoveBonus} />
      <LineEditor title="Удръжки" lines={row.deductions} onAdd={onAddDeduction} onRemove={onRemoveDeduction} />

      <div className="table-wrap" style={{ marginTop: 8 }}>
        <table className="compact">
          <tbody>
            <tr>
              <td><strong>Сума за вземане</strong> <span className="muted">(след бонуси, удръжки и отпуски; ваучерите са отделно)</span></td>
              <td className="num"><strong>{money(row.total)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="toolbar">
        {row.lastSaved && (
          <span className="muted">
            записан {new Date(row.lastSaved.at).toLocaleString('bg-BG')} — {money(row.lastSaved.total)}
            {row.savedCount > 1 ? ` (${row.savedCount} версии)` : ''}
            {Math.abs(row.lastSaved.total - row.total) > 0.005 && ' · изчислението вече е различно'}
          </span>
        )}
        <div className="spacer" />
        <button disabled={saving} onClick={onSave}>
          {saving ? 'Записване…' : (row.lastSaved ? 'Запиши нова версия' : 'Запиши фиша')}
        </button>
        <button
          disabled={sending || !row.email || !row.lastSaved}
          title={!row.lastSaved ? 'Първо запиши фиша' : (row.email ? undefined : 'Служителят няма въведен имейл')}
          onClick={onSend}
        >
          {sending ? 'Изпращане…' : 'Изпрати записания по имейл'}
        </button>
      </div>
    </div>
  );
}

function LineEditor({ title, lines, onAdd, onRemove }) {
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const total = lines.reduce((s, l) => s + Number(l.amount), 0);

  return (
    <div style={{ marginTop: 8 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>{title} ({money(total)})</div>
      {lines.map((l) => (
        <div key={l.id} className="toolbar" style={{ padding: '2px 0' }}>
          <span style={{ flex: 1 }}>{l.label}</span>
          <span>{money(l.amount)}</span>
          <button className="link" onClick={() => onRemove(l.id)}>премахни</button>
        </div>
      ))}
      <div className="toolbar">
        <input placeholder="описание" value={label} onChange={(e) => setLabel(e.target.value)} autoComplete="off" style={{ flex: 1 }} />
        <input placeholder="сума" type="number" step="0.01" style={{ width: 90 }}
          value={amount} onChange={(e) => setAmount(e.target.value)} />
        <button
          disabled={!label.trim() || !amount}
          onClick={() => { onAdd(label.trim(), amount); setLabel(''); setAmount(''); }}
        >
          добави
        </button>
      </div>
    </div>
  );
}

/** „2026-09-03" → „3.9." — кратко, за списъка с дни в отпуск. */
function kratkaData(iso) {
  const [, m, d] = iso.split('-').map(Number);
  return `${d}.${m}.`;
}

/** Последователните дни се събират в диапазон: [{from, to, count}]. */
function diapazoni(dni) {
  const out = [];
  for (const d of dni) {
    const last = out[out.length - 1];
    if (last && new Date(`${last.to}T00:00:00Z`).getTime() + 86400000 === new Date(`${d}T00:00:00Z`).getTime()) {
      last.to = d;
      last.count += 1;
    } else {
      out.push({ from: d, to: d, count: 1 });
    }
  }
  return out;
}

function Diapazon({ g }) {
  return (
    <>
      {g.from === g.to ? kratkaData(g.from) : `${kratkaData(g.from)} – ${kratkaData(g.to)}`}
      {' '}({g.count} {g.count === 1 ? 'ден' : 'дни'})
    </>
  );
}

function VacationEditor({ calendarDays, manualDays, total, hasDeliveryUser, onAdd, onRemove }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginTop: 8 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
        Отпуск ({total} работни дни) — без процент от оборота за тях
      </div>
      {diapazoni(calendarDays ?? []).map((g) => (
        <div key={`c${g.from}`} style={{ padding: '2px 0' }}>
          <Diapazon g={g} /> <span className="muted">· от календара на Деливери</span>
        </div>
      ))}
      {!hasDeliveryUser && (
        <div className="muted" style={{ fontSize: 12 }}>
          Няма свързан потребител в Деливери — отпуските от календара не могат да се вземат.
        </div>
      )}
      {diapazoni(manualDays ?? []).map((g) => (
        <div key={`m${g.from}`} className="toolbar" style={{ padding: '2px 0' }}>
          <span style={{ flex: 1 }}><Diapazon g={g} /> <span className="muted">· ръчно</span></span>
          <button className="link" onClick={() => onRemove(g.from, g.to)}>премахни</button>
        </div>
      ))}
      {!open && <button className="link" onClick={() => setOpen(true)}>+ ръчно добавяне на отпуск</button>}
      {open && (
        <div className="toolbar">
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); if (!to) setTo(e.target.value); }} />
          <span>до</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <button
            disabled={!from || !to}
            onClick={() => { onAdd(from, to); setFrom(''); setTo(''); setOpen(false); }}
          >
            добави
          </button>
        </div>
      )}
    </div>
  );
}
